import React, { useMemo } from 'react';
import { 
  Clock, 
  AlertTriangle, 
  Calendar,
  Zap,
  CheckCircle2
} from 'lucide-react';

const FiltrosRapidosCompactos = ({
  filtroAtivo,
  setFiltroAtivo,
  contasFiltradas = [],
  totalContas = 0,
  contasRecebidas = [],
  contasReceber = []
}) => {
  const counts = useMemo(() => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const fimDia = new Date(hoje);
    fimDia.setHours(23, 59, 59, 999);
    const proximos7 = new Date(hoje);
    proximos7.setDate(hoje.getDate() + 7);
    proximos7.setHours(23, 59, 59, 999);

    let vencidas = 0;
    let hojeCount = 0;
    let proximas7 = 0;

    contasReceber.forEach((conta) => {
      const dataRaw = conta.data_vencimento_convertida || conta.dataVencimento;
      const data = dataRaw instanceof Date ? new Date(dataRaw) : new Date(dataRaw);
      if (Number.isNaN(data.getTime())) return;

      data.setHours(0, 0, 0, 0);
      if (data < hoje) vencidas += 1;
      if (data >= hoje && data <= fimDia) hojeCount += 1;
      if (data >= hoje && data <= proximos7) proximas7 += 1;
    });

    return {
      total: totalContas,
      vencidas,
      hoje: hojeCount,
      proxima_vencimento: proximas7,
      recebidas: contasRecebidas.length
    };
  }, [contasReceber, contasRecebidas.length, totalContas]);

  const filtrosRapidos = useMemo(() => ([
    {
      id: 'todas',
      label: 'Todas',
      icon: null,
      color: 'gray',
      count: counts.total
    },
    {
      id: 'vencidas',
      label: 'Vencidas',
      icon: AlertTriangle,
      color: 'red',
      count: counts.vencidas
    },
    {
      id: 'hoje',
      label: 'Hoje',
      icon: Clock,
      color: 'orange',
      count: counts.hoje
    },
    {
      id: 'proxima_vencimento',
      label: 'Próximas 7 dias',
      icon: Calendar,
      color: 'yellow',
      count: counts.proxima_vencimento
    },
    {
      id: 'recebidas',
      label: 'Recebidas',
      icon: CheckCircle2,
      color: 'green',
      count: counts.recebidas
    }
  ]), [counts]);

  const getColorClasses = (color, isActive = false) => {
    const baseClasses = "px-3 py-2 rounded-[var(--cp-radius-pill,999px)] text-sm font-semibold transition-all duration-[var(--cp-motion-fast,180ms)] flex min-h-[44px] items-center gap-2 border motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--cp-surface,var(--surface))]";
    if (isActive) {
      return `${baseClasses} border-transparent bg-[color:var(--accent)] text-white [box-shadow:var(--cp-shadow-glow,none)]`;
    }
    return `${baseClasses} border-[color:var(--cp-border,var(--border))] bg-[color:var(--cp-surface-soft,var(--surface-muted))] text-[color:var(--text-primary)] hover:border-[color:var(--cp-border-strong,var(--border))] hover:bg-[color:var(--cp-surface,var(--surface))]`;
  };

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-[color:var(--accent)] flex-shrink-0" />
          <h3 className="text-sm font-semibold text-[color:var(--text-primary)]">Filtros Rápidos</h3>
        </div>
        <div className="text-xs text-[color:var(--text-muted)] whitespace-nowrap">
          {contasFiltradas.length} de {totalContas} contas
        </div>
      </div>

      <div className="app-scroll-subtle -mx-1 flex w-[calc(100%+0.5rem)] gap-2 overflow-x-auto px-1 pb-1 md:mx-0 md:w-full md:flex-wrap md:overflow-visible md:px-0">
        {filtrosRapidos.map((filtro) => {
          const Icon = filtro.icon;
          const isActive = filtroAtivo === filtro.id;
          
          return (
            <button
              key={filtro.id}
              onClick={() => setFiltroAtivo(filtro.id)}
              className={`${getColorClasses(filtro.color, isActive)} shrink-0`}
              type="button"
              aria-pressed={isActive}
            >
              {Icon && <Icon className="h-4 w-4 flex-shrink-0" />}
              <span className="whitespace-nowrap">{filtro.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs flex-shrink-0 ${isActive ? 'bg-white/90 text-[color:var(--accent)]' : 'bg-[color:var(--cp-surface,var(--surface))] text-[color:var(--text-muted)]'}`}>
                {filtro.count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default FiltrosRapidosCompactos;
